import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

function Guide({ id, title, badge, children, open, onToggle }) {
  return (
    <div className="bg-white border border-gray-200" data-testid={`help-guide-${id}`}>
      <button onClick={() => onToggle(id)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`help-toggle-${id}`}>
        <span className="font-display font-bold flex items-center gap-2">
          {title}
          {badge && <span className="text-[9px] font-black tracking-widest uppercase bg-[#0A1C40] text-white px-1.5 py-0.5">{badge}</span>}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm leading-relaxed space-y-3">{children}</div>}
    </div>
  );
}

const Ol = ({ items }) => (
  <ol className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="shrink-0 w-6 h-6 rounded-full bg-[#0A1C40] text-white text-xs font-black flex items-center justify-center">{i + 1}</span>
        <span>{it}</span>
      </li>
    ))}
  </ol>
);

export default function AdminHelp() {
  const [open, setOpen] = useState("staff");
  const toggle = (id) => setOpen((o) => (o === id ? null : id));

  return (
    <div className="max-w-3xl" data-testid="admin-help-page">
      <div className="ipv-eyebrow text-[#0A1C40] flex items-center gap-1"><HelpCircle className="w-3.5 h-3.5" /> Plain-language guides</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Help & How-To</h1>
      <p className="text-sm text-gray-600 mt-2 mb-6">
        Everything your team needs to run the store. Cashiers also have a <b>? Help</b> button inside the register (POS),
        and drivers have one inside the Driver app.
      </p>

      <div className="space-y-3">
        <Guide id="staff" title="Give an employee their own login" badge="Owner" open={open === "staff"} onToggle={toggle}>
          <Ol items={[
            <>Go to <b>Staff & Roles</b> in the left menu and tap <b>+ New staff</b>.</>,
            <>Enter their <b>email</b>, <b>name</b>, pick a <b>role</b> (see "Who can see what" below), and set a <b>password</b>. For managers, also set their 4-digit <b>PIN</b> (used to approve discounts/refunds at the register).</>,
            <>Tap <b>Save</b>. Tell them to log in at <b>ipvaluemarketplace.com/login</b> with that email + password.</>,
            <>The system sends them to the right place automatically: cashiers/associates land in the <b>register (POS)</b>, drivers land in the <b>Driver app</b>, managers land in POS.</>,
            <>Someone leaves? Open Staff & Roles and <b>deactivate</b> them — their login stops working instantly.</>,
          ]} />
        </Guide>

        <Guide id="roles" title="Who can see what (roles explained)" open={open === "roles"} onToggle={toggle}>
          <div className="overflow-x-auto">
          <table className="w-full text-sm border border-gray-200 min-w-[420px]">
            <thead className="bg-gray-50 ipv-eyebrow text-left"><tr><th className="p-2">Role</th><th className="p-2">What they can do</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="p-2 font-bold">Owner (you)</td><td className="p-2">Everything — products, orders, staff, Reports (revenue & margin), settings.</td></tr>
              <tr><td className="p-2 font-bold">Manager</td><td className="p-2">Register + PIN approvals + store's daily totals. <b>Cannot</b> see your costs, margins or reports.</td></tr>
              <tr><td className="p-2 font-bold">Associate / Cashier</td><td className="p-2">Register only — ring up sales, send payment links. No admin access.</td></tr>
              <tr><td className="p-2 font-bold">Driver</td><td className="p-2">Today's delivery stops only. Never sees prices, totals or payment info.</td></tr>
            </tbody>
          </table>
          </div>
        </Guide>

        <Guide id="orders" title="Manage orders (statuses, texts & emails)" open={open === "orders"} onToggle={toggle}>
          <Ol items={[
            <>Go to <b>Orders</b>. Use the search box or the Pending/Paid filters. <b>Tap a row</b> to open the full order.</>,
            <>Move the order through its stages with the status buttons: <b>Paid → Preparing → Ready for pickup</b> (pickups) or <b>Out for delivery → Completed</b> (deliveries).</>,
            <>Every status change <b>automatically texts and emails the customer</b> — you never have to notify them by hand.</>,
            <>Delivery orders: the <b>Scheduled delivery</b> box shows the date. Pick a new date and tap <b>Reschedule + notify</b> — the customer is told instantly.</>,
            <>Watch for <b>⚠ flags</b> — if a driver marks "not home / refused" you'll also get an email; just reschedule the delivery.</>,
            <>Cancel an order with the <b>Cancel</b> status — customer gets notified. Delivered orders show the customer's signature and any damage photo.</>,
          ]} />
        </Guide>

        <Guide id="pos" title="The register (POS) — ring up walk-in sales" badge="Staff" open={open === "pos"} onToggle={toggle}>
          <Ol items={[
            <>Staff log in and are taken straight to the register at <b>/pos</b>.</>,
            <>Tap products (or search) to build the cart. Add the customer's <b>phone</b> so they get a text receipt.</>,
            <>Take payment: <b>Cash</b> (enter what they handed you — it shows the change), <b>Card · Tap</b> (opens Square), or <b>Send payment link</b> (they pay on their own phone within 48 h).</>,
            <>The <b>? Help</b> button in the POS header shows your cashiers these exact steps.</>,
          ]} />
        </Guide>

        <Guide id="paylink" title="Payment links — charge customers by text/email" open={open === "paylink"} onToggle={toggle}>
          <Ol items={[
            <>Perfect for phone orders and "I'll pay when I get paid Friday" customers.</>,
            <>In POS, build the cart → <b>Send payment link</b> → enter their name + phone or email.</>,
            <>They get a secure page with your Square card form. When they pay, the order flips to <b>PAID</b> in Orders and they get a receipt automatically.</>,
            <>Links expire after <b>48 hours</b>. Sent links are listed for staff, and you can copy/re-send the link from the confirmation panel.</>,
          ]} />
        </Guide>

        <Guide id="delivery" title="Delivery days, cutoffs & blocking dates" open={open === "delivery"} onToggle={toggle}>
          <Ol items={[
            <>You deliver <b>Tuesday · Thursday · Saturday</b>. Customers must order by <b>2 PM the day before</b>. Saturday window is 9 AM–1 PM.</>,
            <>Holiday or van full? Go to <b>Settings → Delivery Schedule</b>, pick the date and tap <b>Block date</b> — it disappears from checkout immediately. Unblock anytime.</>,
            <>Fees are automatic: FREE on $299+ within 10 miles, tiered beyond that, $75/$99 pallet flat rates, +$75 rush.</>,
          ]} />
        </Guide>

        <Guide id="driver" title="The Driver app — how deliveries get closed" badge="Driver" open={open === "driver"} onToggle={toggle}>
          <Ol items={[
            <>Your driver logs in and sees <b>only today's stops</b> — no prices, no payment info, plus a permanent "you do not collect money" banner.</>,
            <>Their flow: <b>Start delivery</b> (texts the customer) → <b>Arrived</b> → <b>Mark DELIVERED</b>, which requires the customer's <b>on-screen signature</b> and either a <b>no-damage checkbox</b> or a <b>damage photo</b>.</>,
            <>"Not home / refused" flags the order, emails you, and keeps it open so you can reschedule.</>,
            <>The <b>? Help</b> button inside the Driver app walks them through this.</>,
          ]} />
        </Guide>

        <Guide id="products" title="Products, pricing & your cost" open={open === "products"} onToggle={toggle}>
          <Ol items={[
            <>Add or edit items in <b>Products</b>. "Compare at" shows the crossed-out MSRP; <b>"Your cost"</b> is private — customers can never see it, and it powers your margin numbers in Reports.</>,
            <>Bulk add: <b>CSV import</b> (Products page) supports a <b>cost</b> column. Or text product photos to the store WhatsApp and AI drafts the listing.</>,
            <>Broke a pallet into 40 items? In <b>Reports → Products missing cost</b>, tick the items, enter the pallet's total cost, and it splits evenly — then override any single item.</>,
          ]} />
        </Guide>

        <Guide id="reports" title="Reports — revenue, margin, staff & pallets" badge="Owner" open={open === "reports"} onToggle={toggle}>
          <Ol items={[
            <>Open <b>Reports</b> and pick a range (Today / 7 / 30 / 90 days / Year).</>,
            <>You'll see revenue, average order, <b>margin %</b> (needs costs entered), web-vs-register-vs-paylink split, staff leaderboard, top products, category margins and pallet performance.</>,
            <>The amber banner counts products with <b>no cost entered</b> — fill those in so your margin picture is complete. Only you can see this page.</>,
          ]} />
        </Guide>

        <Guide id="reviews" title="Google review requests" open={open === "reviews"} onToggle={toggle}>
          <Ol items={[
            <>Go to <b>Settings → Google Review Requests</b> and paste your Google Business Profile review link (Business Profile → "Ask for reviews").</>,
            <>After every completed order, the customer automatically gets a text + email asking for a review — sent <b>3 hours</b> after delivery (you can change the delay).</>,
            <>Until you paste the link, <b>nothing sends</b> — so do it as soon as your profile is live.</>,
          ]} />
        </Guide>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        Still stuck? Every screen is safe to explore — nothing charges a customer without a payment step. Store line: (616) 410-0158.
      </div>
    </div>
  );
}
